/* eslint-disable no-undef */
import {
  useState,
  useCallback,
  SetStateAction,
  Dispatch,
  useMemo,
  useEffect,
  useLayoutEffect,
} from 'react'
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DrawingManager,
} from '@react-google-maps/api'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { useParams } from 'react-router-dom'
import { usePlace } from 'hooks/usePlaces'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { ImportVectorModal } from './ImportVectorModal'
import { getColorFromIndex, getPolygonColor } from './utils'
import { ServiceAreaToolbar } from './ServiceAreaToolbar'
import { MapButtons } from './MapButtons'
import { googleMapsStyle } from './GoogleMapsStyle'
import './ServiceAreas.css'
import { Polygon } from './Polygon'

const defaultMapOptions = {
  fullscreenControl: false,
  streetViewControl: false,
  mapTypeControl: false,
  styles: googleMapsStyle,
}

type ServiceAreaMapProps = {
  drawnPath: { lat: number; lng: number }[][] | []
  setDrawnPath: Dispatch<SetStateAction<{ lat: number; lng: number }[][] | []>>
  overlays: google.maps.Polygon[] | []
  setOverlays: Dispatch<SetStateAction<google.maps.Polygon[] | []>>
  isEditing: boolean
  setIsEditing: Dispatch<SetStateAction<boolean>>
  deleteShapes: (overlays: google.maps.Polygon[] | []) => void
  deliveryAreaId: number | null
  setDeliveryAreaId: Dispatch<SetStateAction<number | null>>
}

const mapLibraries: (
  | 'drawing'
  | 'geometry'
  | 'localContext'
  | 'places'
  | 'visualization'
)[] = ['drawing']

export const ServiceAreaMap = ({
  drawnPath,
  setDrawnPath,
  setOverlays,
  overlays,
  isEditing,
  setIsEditing,
  deleteShapes,
  deliveryAreaId,
  setDeliveryAreaId,
}: ServiceAreaMapProps) => {
  const {
    allDeliveryAreas,
    coordinates,
    storeLatitude,
    storeLongitude,
    selectedDeliveryArea,
    collectionPoints,
    selectedCollectionPoint,
    setCoordinates,
    isFetchingCoordinates,
    isLoadingCollectionPoints,
  } = useServiceAreas()

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey:
      `${window.appConfig.GOOGLE_MAPS_KEY}&channel=200` as string,
    libraries: mapLibraries,
    version: '3.55',
  })

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const { placeId } = useParams()
  const [importVectorModal, setImportVectorModal] = useState({
    right: false,
  })
  const [drawingModeEnabled, setDrawingModeEnabled] = useState<number | null>(
    null
  )
  const { isLoading, error, place } = usePlace(placeId as string)
  const containerStyle = {
    height: selectedDeliveryArea.id ? 'calc(100vh - 55px)' : '100vh',
  }

  const handleOpenVectorModal = () => {
    setImportVectorModal({ right: true })
  }
  const handleCloseModal = () => {
    setImportVectorModal({ right: false })
  }

  function getPaths(polygon: google.maps.Polygon) {
    const polygonBounds = polygon.getPath()
    const bounds = []
    for (let i = 0; i < polygonBounds.getLength(); i += 1) {
      const point = {
        lat: polygonBounds.getAt(i).lat(),
        lng: polygonBounds.getAt(i).lng(),
      }
      bounds.push(point)
    }
    return bounds
  }

  const onPolygonComplete = (value: google.maps.Polygon) => {
    const paths = getPaths(value)
    setDrawnPath([paths])
    setOverlays([...overlays, value])
    setDrawingModeEnabled(null)
    setIsEditing(true)
  }

  const getMapCenterCoordinates = () => {
    const backupCoordinates = {
      lat: storeLatitude || 60.163678,
      lng: storeLongitude || 24.925887,
    }
    if (selectedDeliveryArea.id) {
      const newCoordinates =
        (coordinates &&
          coordinates.length > 0 && {
            lat: Number((coordinates[0][0].area as number[][])[0][0]),
            lng: Number((coordinates[0][0].area as number[][])[0][1]),
          }) ||
        backupCoordinates
      return newCoordinates
    }
    if (selectedCollectionPoint.id) {
      return {
        lat: Number(selectedCollectionPoint.latitude) || 60.163678,
        lng: Number(selectedCollectionPoint.longitude) || 24.925887,
      }
    }
    return backupCoordinates
  }

  const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>()
  const [zoom, setZoom] = useState<number | undefined>(8)

  const resetMap = () => {
    if (map) {
      const initialCenter = {
        lat: getMapCenterCoordinates().lat,
        lng: getMapCenterCoordinates().lng,
      }
      map.setZoom(8)
      map.setCenter(initialCenter)
      deleteShapes(overlays)
    }
  }

  useEffect(() => {
    resetMap()
  }, [selectedCollectionPoint, selectedDeliveryArea])

  useLayoutEffect(() => {
    resetMap()
  }, [])

  const onLoad = useCallback(function callback(map) {
    const initialCenter = {
      lat: getMapCenterCoordinates().lat,
      lng: getMapCenterCoordinates().lng,
    }
    setCenter(initialCenter)
    setZoom(8)
    setMap(map)
  }, [])

  const onUnmount = useCallback(function callback() {
    setMap(null)
  }, [])

  const memoizedPolygons = useMemo(() => {
    if (!coordinates || coordinates.length === 0) {
      return false
    }
    return coordinates.map((coordinateGroup) =>
      coordinateGroup.map((group) => {
        return (
          <Polygon
            key={group.id || 'group 1'}
            group={group}
            deliveryAreaId={deliveryAreaId}
            setDrawnPath={setDrawnPath}
            setIsEditing={setIsEditing}
            setDeliveryAreaId={setDeliveryAreaId}
            drawnPath={drawnPath}
          />
        )
      })
    )
  }, [coordinates, allDeliveryAreas, isFetchingCoordinates])

  const memoizedMarkers = useMemo(() => {
    if (
      !isLoadingCollectionPoints &&
      (!collectionPoints || collectionPoints.length === 0)
    ) {
      return false
    }
    return collectionPoints.map((collectionPoint) => {
      const index = collectionPoints.findIndex(
        (p) => p.id === collectionPoint.id
      )
      if (!collectionPoint.longitude || !collectionPoint.latitude) return null
      return (coordinates && coordinates.length > 1) ||
        selectedCollectionPoint.id === collectionPoint.id ? (
        <Marker
          key={collectionPoint.id}
          title={collectionPoint.name}
          position={{
            lat: collectionPoint.latitude,
            lng: collectionPoint.longitude,
          }}
          icon={{
            fillColor: getColorFromIndex(index),
            fillOpacity: 1,
            scale: 1.6,
            strokeWeight: 0.1,
            strokeColor: '#ffffff',
            path: 'M10 1C6.683 1 4 3.683 4 7c0 4.5 6 11.143 6 11.143S16 11.5 16 7c0-3.317-2.683-6-6-6zm0 9.214a3.215 3.215 0 0 1 0-6.428 3.215 3.215 0 0 1 0 6.428z',
          }}
        />
      ) : (
        <div key={collectionPoint.id} />
      )
    })
  }, [
    collectionPoints,
    selectedCollectionPoint,
    isLoadingCollectionPoints,
    coordinates,
  ])

  const updateMap = () => {
    if (map) {
      const zoomLevel = map.getZoom()
      if (zoomLevel !== zoom) {
        setZoom(zoomLevel)
      }
      const mapCenter = map.getCenter()
      if (
        mapCenter &&
        mapCenter?.lat() !== center?.lat &&
        mapCenter?.lng() !== center?.lng
      ) {
        setCenter({ lat: mapCenter?.lat(), lng: mapCenter?.lng() })
      }
    }
  }

  if (
    isLoading ||
    !place ||
    !isLoaded ||
    isFetchingCoordinates ||
    isLoadingCollectionPoints
  )
    return <LoadingIndicator />
  if (error) return <h1>Error</h1>

  return (
    <>
      <ServiceAreaToolbar
        isEditing={isEditing}
        deliveryAreaId={deliveryAreaId}
        drawnPath={drawnPath}
        setDeliveryAreaId={setDeliveryAreaId}
        deleteShapes={deleteShapes}
        overlays={overlays}
        drawingModeEnabled={drawingModeEnabled}
        setDrawingModeEnabled={setDrawingModeEnabled}
        setDrawnPath={setDrawnPath}
        setIsEditing={setIsEditing}
      />
      {Boolean(selectedDeliveryArea.id) && !isEditing && (
        <MapButtons
          drawingModeEnabled={drawingModeEnabled}
          drawnPath={drawnPath}
          setDrawingModeEnabled={setDrawingModeEnabled}
          setCoordinates={setCoordinates}
          handleOpenVectorModal={handleOpenVectorModal}
        />
      )}
      <GoogleMap
        center={center}
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        onZoomChanged={updateMap}
        onCenterChanged={updateMap}
        onUnmount={onUnmount}
        options={defaultMapOptions}
        zoom={zoom}>
        {drawingModeEnabled === selectedDeliveryArea.id ? (
          <DrawingManager
            onPolygonComplete={onPolygonComplete}
            drawingMode={
              drawingModeEnabled === selectedDeliveryArea.id
                ? google.maps.drawing.OverlayType.POLYGON
                : null
            }
            options={{
              drawingControl: Boolean(
                drawingModeEnabled === selectedDeliveryArea.id
              ),
              drawingControlOptions: {
                position: google.maps.ControlPosition.TOP_RIGHT,
                drawingModes: [google.maps.drawing.OverlayType.POLYGON],
              },
              polygonOptions: {
                fillColor: getPolygonColor(
                  selectedDeliveryArea.id,
                  allDeliveryAreas
                ),
                fillOpacity: 0.35,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                strokeColor: getPolygonColor(
                  selectedDeliveryArea.id,
                  allDeliveryAreas
                ),
              },
            }}
          />
        ) : null}
        {memoizedPolygons}
        {memoizedMarkers}
        {Boolean(selectedDeliveryArea.id) && (
          <ImportVectorModal
            handleCloseVectorModal={handleCloseModal}
            importVectorModal={importVectorModal}
            setImportVectorModal={setImportVectorModal}
            setIsEditing={setIsEditing}
          />
        )}
      </GoogleMap>
    </>
  )
}
